import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/pages/admin/api";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState(1);
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      if (r.data.reset_token) {
        setToken(r.data.reset_token);
        toast.success("Reset token generated (SMTP disabled, using demo mode).");
      } else {
        toast.success("If the email is registered, a reset link will be sent.");
      }
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      toast.success("Password reset. Please sign in.");
      navigate("/admin/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 dark:bg-slate-950 ambient-glow px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <span className="w-11 h-11 rounded-xl bg-blue-600 text-white grid place-items-center shadow-md"><FileText size={20} /></span>
          <div>
            <div className="font-heading font-bold text-lg leading-tight">Simple Invoice</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Reset Password</div>
          </div>
        </div>

        <div className="rounded-2xl bg-card border border-border p-8 shadow-xl">
          <Link to="/admin/login" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={12} /> Back to login</Link>
          {step === 1 ? (
            <form onSubmit={requestReset}>
              <h1 className="font-heading text-2xl font-bold tracking-tight mb-1">Forgot your password?</h1>
              <p className="text-sm text-muted-foreground mb-6">Enter your admin email and we&apos;ll issue a reset token.</p>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Email</Label>
              <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mb-4" />
              <Button data-testid="request-reset" type="submit" disabled={loading} className="w-full rounded-xl h-11 bg-blue-600 hover:bg-blue-700 text-white">
                {loading ? "Requesting..." : "Send reset token"}
              </Button>
            </form>
          ) : (
            <form onSubmit={resetPassword}>
              <h1 className="font-heading text-2xl font-bold tracking-tight mb-1">Set new password</h1>
              <p className="text-sm text-muted-foreground mb-6">Paste the reset token and choose a new password.</p>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Reset token</Label>
              <Input data-testid="reset-token" value={token} onChange={(e) => setToken(e.target.value)} required className="mb-4 font-mono text-xs" />
              <Label className="text-xs text-muted-foreground mb-1.5 block">New password</Label>
              <Input data-testid="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} className="mb-4" />
              <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full rounded-xl h-11 bg-blue-600 hover:bg-blue-700 text-white">
                <KeyRound size={14} className="mr-2" /> {loading ? "Resetting..." : "Reset password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
